import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaCalculator,
  FaGraduationCap,
  FaUser,
  FaIdCard,
  FaBook,
  FaChartLine,
  FaDownload,
  FaShare,
  FaPrint,
  FaCheck,
  FaSpinner,
  FaArrowLeft,
  FaArrowRight,
  FaUniversity,
  FaCalendarAlt,
  FaInfoCircle,
  FaTrophy,
  FaAward,
  FaShieldAlt,
  FaRocket,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
// Import the Footer context
import { useFooter } from "../../context/FooterContext";

const SGPACalculator = () => {
  const { user } = useSelector((state) => state.auth);
  const { setShowFooter } = useFooter(); // Add this line
  const [step, setStep] = useState(1);
  const [branches, setBranches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    branch: "",
    semester: "",
    cycle: "",
  });

  const userScheme = user?.scheme || "2022";

  const [studentDetails, setStudentDetails] = useState({
    name: user?.name || "",
    usn: user?.usn || "",
    branch: user?.branch || "",
  });

  const [marks, setMarks] = useState({});
  const resultRef = useRef(null);

  // Hide footer when component mounts, show when unmounts
  useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Check dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    };

    checkDarkMode();

    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    if (formData.branch && formData.semester) {
      fetchSubjects();
    }
  }, [formData.branch, formData.semester, formData.cycle]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(
        "/api/sgpa/branches",
      );
      setBranches(response.data);
      if (user?.branch && branches.length > 0) {
        setFormData((prev) => ({ ...prev, branch: user.branch }));
      }
    } catch (error) {
      toast.error("Failed to fetch branches");
    }
  };

  const fetchSubjects = async () => {
    setLoading(true);
    try {
      const params = {
        scheme: userScheme,
        branch: formData.branch,
        semester: formData.semester,
      };
      if (formData.semester <= 2 && formData.cycle) {
        params.cycle = formData.cycle;
      }

      const response = await axios.get(
        "/api/sgpa/subjects",
        { params },
      );
      setSubjects(response.data);

      const initialMarks = {};
      response.data.forEach((subject) => {
        initialMarks[subject.subjectCode] = "";
      });
      setMarks(initialMarks);
    } catch (error) {
      toast.error("Failed to fetch subjects");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "semester") {
      setFormData((prev) => ({ ...prev, cycle: "" }));
    }
  };

  const handleMarksChange = (subjectCode, value) => {
    setMarks((prev) => ({ ...prev, [subjectCode]: value }));
  };

  const calculateSGPA = async () => {
    if (!formData.branch || !formData.semester) {
      toast.error("Please complete all selections");
      return;
    }

    if (formData.semester <= 2 && !formData.cycle) {
      toast.error("Please select a cycle");
      return;
    }

    const missingMarks = subjects.filter(
      (s) => !marks[s.subjectCode] && marks[s.subjectCode] !== 0,
    );
    if (missingMarks.length > 0) {
      toast.error(`Please enter marks for all subjects`);
      return;
    }

    setCalculating(true);
    try {
      const response = await axios.post(
        "/api/sgpa/calculate",
        {
          scheme: userScheme,
          branch: formData.branch,
          semester: formData.semester,
          cycle: formData.cycle,
          marks,
          studentDetails,
        },
      );
      setResult(response.data);
      setStep(3);

      await saveSGPA(response.data);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to calculate SGPA");
    } finally {
      setCalculating(false);
    }
  };

  const saveSGPA = async (calculatedResult) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "/api/academic/save-sgpa",
        {
          semester: formData.semester,
          sgpa: calculatedResult.sgpa,
          percentage: calculatedResult.percentage,
          classAwarded: calculatedResult.classAwarded,
          subjectDetails: calculatedResult.subjectResults,
          scheme: userScheme,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("SGPA saved to your academic record!");
    } catch (error) {
      console.error("Failed to save SGPA:", error);
    }
  };

  const downloadResult = async () => {
    if (resultRef.current) {
      try {
        const loadingToast = toast.loading("Generating PDF...");

        const actionButtons =
          resultRef.current.querySelectorAll(".action-buttons");
        const originalDisplay = [];
        actionButtons.forEach((btn, i) => {
          originalDisplay[i] = btn.style.display;
          btn.style.display = "none";
        });

        await new Promise((resolve) => setTimeout(resolve, 200));

        const element = resultRef.current;
        const canvas = await html2canvas(element, {
          scale: 3,
          backgroundColor: "#ffffff",
          logging: false,
          useCORS: true,
        });

        const imgData = canvas.toDataURL("image/png", 1.0);
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });

        const imgWidth = 190;
        const pageHeight = 277;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 10;

        pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          position = heightLeft - imgHeight + 10;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        pdf.save(`SGPA_Result_${studentDetails.usn || "student"}.pdf`);

        actionButtons.forEach((btn, i) => {
          btn.style.display = originalDisplay[i];
        });

        toast.dismiss(loadingToast);
        toast.success("PDF downloaded successfully");
      } catch (error) {
        console.error("Download error:", error);
        toast.error("Failed to download PDF");
      }
    }
  };

  const printResult = () => {
    const printWindow = window.open("", "_blank");
    const resultContent = resultRef.current.cloneNode(true);
    const actionButtons = resultContent.querySelectorAll(".action-buttons");
    actionButtons.forEach((btn) => btn.remove());
    printWindow.document.write(`
      <html>
        <head>
          <title>SGPA Result - ${studentDetails.name}</title>
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; }
            .result-card { max-width: 800px; margin: 0 auto; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
            th { background: linear-gradient(135deg, #8b5cf6, #3b82f6); color: white; }
            .header { text-align: center; margin-bottom: 30px; }
            .sgpa { font-size: 48px; color: #8b5cf6; font-weight: bold; }
          </style>
        </head>
        <body>${resultContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const shareResult = () => {
    if (!result) return;

    const text = `📊 VTUGenX SGPA Result

Student: ${studentDetails.name}
USN: ${studentDetails.usn}
Branch: ${formData.branch}
Semester: ${formData.semester}${formData.cycle ? ` (${formData.cycle === "P" ? "Physics Cycle" : "Chemistry Cycle"})` : ""}
Scheme: ${userScheme}

SGPA: ${result.sgpa}
Percentage: ${result.percentage}%
Class: ${result.classAwarded}

Calculate your SGPA at VTUGenX!`;

    if (navigator.share) {
      navigator
        .share({
          title: "VTUGenX SGPA Result",
          text: text,
          url: window.location.href,
        })
        .catch(() => copyToClipboard(text));
    } else {
      copyToClipboard(text);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const resetCalculator = () => {
    setStep(1);
    setResult(null);
    setMarks({});
    setFormData({
      branch: "",
      semester: "",
      cycle: "",
    });
    setStudentDetails({
      name: user?.name || "",
      usn: user?.usn || "",
      branch: user?.branch || "",
    });
  };

  const getBranchName = (code) => {
    const branch = branches.find((b) => b.code === code);
    return branch ? branch.name : code;
  };

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  // Responsive styles
  const containerPadding = isMobile ? "1rem" : "2rem";
  const cardPadding = isMobile ? "1rem" : "1.5rem";
  const headingSize = isMobile ? "1.5rem" : "1.8rem";

  return (
    <div
      style={{
        minHeight: "calc(100vh - 200px)",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        padding: containerPadding,
        position: "relative",
      }}
    >
      {/* Animated Background Grid */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating Gradient Orbs */}
      <motion.div
        animate={{
          x: [0, 100, 0],
          y: [0, 50, 0],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          top: "10%",
          left: "-5%",
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <motion.div
        animate={{
          x: [0, -100, 0],
          y: [0, 80, 0],
        }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          bottom: "10%",
          right: "-5%",
          width: "350px",
          height: "350px",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div
        style={{
          maxWidth: "1000px",
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Header with Back Button */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1.5rem",
            flexWrap: "wrap",
            gap: "1rem",
          }}
        >
          <Link
            to="/dashboard"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              color: "#8b5cf6",
              textDecoration: "none",
              fontSize: "0.9rem",
              fontWeight: 500,
              padding: "0.5rem 1rem",
              borderRadius: "10px",
              background: isDarkMode
                ? "rgba(139,92,246,0.1)"
                : "rgba(139,92,246,0.05)",
              transition: "all 0.3s",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = isDarkMode
                ? "rgba(139,92,246,0.2)"
                : "rgba(139,92,246,0.1)";
              e.target.style.transform = "translateX(-5px)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = isDarkMode
                ? "rgba(139,92,246,0.1)"
                : "rgba(139,92,246,0.05)";
              e.target.style.transform = "translateX(0)";
            }}
          >
            <FaArrowLeft /> Back to Dashboard
          </Link>

          {/* Status Badge */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.3rem 0.8rem",
              borderRadius: "20px",
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              color: "white",
              fontSize: "0.7rem",
            }}
          >
            <FaShieldAlt size={10} /> SGPA Calculator
          </div>
        </div>

        {/* Header Title */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, type: "spring", stiffness: 300 }}
          style={{
            textAlign: "center",
            marginBottom: isMobile ? "1.5rem" : "2rem",
          }}
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
            style={{ display: "inline-block", marginBottom: "1rem" }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                width: "70px",
                height: "70px",
                borderRadius: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
                boxShadow: "0 20px 40px -12px rgba(139,92,246,0.4)",
              }}
            >
              <FaCalculator size={32} color="white" />
            </div>
          </motion.div>
          <h1
            style={{
              fontSize: headingSize,
              fontWeight: "bold",
              marginBottom: "0.5rem",
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            VTU SGPA Calculator
          </h1>
          <p
            style={{
              fontSize: isMobile ? "0.8rem" : "0.9rem",
              color: getTextSecondary(),
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            Accurate SGPA calculation based on VTU guidelines with dynamic
            subject management
          </p>
          <div
            style={{
              display: "inline-block",
              marginTop: "0.5rem",
              padding: "0.3rem 1rem",
              background: isDarkMode
                ? "rgba(139,92,246,0.1)"
                : "rgba(139,92,246,0.05)",
              borderRadius: "20px",
              fontSize: isMobile ? "0.65rem" : "0.7rem",
              color: "#8b5cf6",
              fontWeight: 500,
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            Your Scheme: CBCS {userScheme} Scheme
          </div>
        </motion.div>

        {/* Step 1: Selection */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "28px",
              boxShadow: isDarkMode
                ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                : "0 25px 50px -12px rgba(0,0,0,0.25)",
              overflow: "hidden",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
                padding: isMobile ? "1rem" : "1.5rem",
                color: "white",
              }}
            >
              <h2
                style={{
                  fontSize: isMobile ? "1.1rem" : "1.2rem",
                  fontWeight: "bold",
                  marginBottom: "0.3rem",
                  color: "white",
                }}
              >
                Select Your Details
              </h2>
              <p
                style={{
                  color: "rgba(255,255,255,0.8)",
                  fontSize: isMobile ? "0.7rem" : "0.75rem",
                }}
              >
                Your scheme is fixed to {userScheme}. Choose branch and semester
              </p>
            </div>

            <div style={{ padding: cardPadding }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: isMobile ? "1rem" : "1.5rem",
                  marginBottom: "1.5rem",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      color: getTextSecondary(),
                      fontWeight: "500",
                      marginBottom: "0.3rem",
                      fontSize: isMobile ? "0.7rem" : "0.75rem",
                    }}
                  >
                    <FaUniversity
                      style={{
                        marginRight: "0.3rem",
                        color: "#8b5cf6",
                        display: "inline",
                      }}
                    />
                    Branch
                  </label>
                  <select
                    value={formData.branch}
                    onChange={(e) => handleFormChange("branch", e.target.value)}
                    style={{
                      width: "100%",
                      padding: isMobile ? "0.6rem" : "0.8rem",
                      border: `2px solid ${getBorderColor()}`,
                      borderRadius: "12px",
                      fontSize: isMobile ? "0.8rem" : "0.85rem",
                      outline: "none",
                      transition: "all 0.3s",
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                    onBlur={(e) =>
                      (e.target.style.borderColor = getBorderColor())
                    }
                  >
                    <option value="">Select Branch</option>
                    {branches.map((branch) => (
                      <option key={branch.code} value={branch.code}>
                        {branch.name} ({branch.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      color: getTextSecondary(),
                      fontWeight: "500",
                      marginBottom: "0.3rem",
                      fontSize: isMobile ? "0.7rem" : "0.75rem",
                    }}
                  >
                    <FaBook
                      style={{
                        marginRight: "0.3rem",
                        color: "#8b5cf6",
                        display: "inline",
                      }}
                    />
                    Semester
                  </label>
                  <select
                    value={formData.semester}
                    onChange={(e) =>
                      handleFormChange("semester", e.target.value)
                    }
                    style={{
                      width: "100%",
                      padding: isMobile ? "0.6rem" : "0.8rem",
                      border: `2px solid ${getBorderColor()}`,
                      borderRadius: "12px",
                      fontSize: isMobile ? "0.8rem" : "0.85rem",
                      outline: "none",
                      transition: "all 0.3s",
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                    onBlur={(e) =>
                      (e.target.style.borderColor = getBorderColor())
                    }
                  >
                    <option value="">Select Semester</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((sem) => (
                      <option key={sem} value={sem}>
                        Semester {sem}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {formData.semester && formData.semester <= 2 && (
                <div style={{ marginBottom: "1.5rem" }}>
                  <label
                    style={{
                      display: "block",
                      color: getTextSecondary(),
                      fontWeight: "500",
                      marginBottom: "0.3rem",
                      fontSize: isMobile ? "0.7rem" : "0.75rem",
                    }}
                  >
                    <FaGraduationCap
                      style={{
                        marginRight: "0.3rem",
                        color: "#8b5cf6",
                        display: "inline",
                      }}
                    />
                    Cycle
                  </label>
                  <div
                    style={{
                      display: "flex",
                      gap: "0.8rem",
                      paddingTop: "0.3rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      onClick={() => handleFormChange("cycle", "P")}
                      style={{
                        flex: 1,
                        padding: isMobile ? "0.5rem" : "0.6rem",
                        background:
                          formData.cycle === "P"
                            ? "linear-gradient(135deg, #8b5cf6, #3b82f6)"
                            : getInputBackground(),
                        color:
                          formData.cycle === "P" ? "white" : getTextColor(),
                        border:
                          formData.cycle === "P"
                            ? "none"
                            : `2px solid ${getBorderColor()}`,
                        borderRadius: "12px",
                        cursor: "pointer",
                        fontWeight: "500",
                        transition: "all 0.3s",
                        fontSize: isMobile ? "0.7rem" : "0.8rem",
                      }}
                    >
                      {formData.semester} Sem (P) - Physics
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      onClick={() => handleFormChange("cycle", "C")}
                      style={{
                        flex: 1,
                        padding: isMobile ? "0.5rem" : "0.6rem",
                        background:
                          formData.cycle === "C"
                            ? "linear-gradient(135deg, #8b5cf6, #3b82f6)"
                            : getInputBackground(),
                        color:
                          formData.cycle === "C" ? "white" : getTextColor(),
                        border:
                          formData.cycle === "C"
                            ? "none"
                            : `2px solid ${getBorderColor()}`,
                        borderRadius: "12px",
                        cursor: "pointer",
                        fontWeight: "500",
                        transition: "all 0.3s",
                        fontSize: isMobile ? "0.7rem" : "0.8rem",
                      }}
                    >
                      {formData.semester} Sem (C) - Chemistry
                    </motion.button>
                  </div>
                </div>
              )}

              <div
                style={{
                  background: isDarkMode
                    ? "rgba(139,92,246,0.1)"
                    : "rgba(139,92,246,0.05)",
                  borderRadius: "12px",
                  padding: "0.8rem",
                  marginBottom: "1.5rem",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.5rem",
                  flexWrap: "wrap",
                  border: `1px solid ${getBorderColor()}`,
                }}
              >
                <FaInfoCircle
                  style={{
                    color: "#8b5cf6",
                    fontSize: isMobile ? "0.9rem" : "1rem",
                    marginTop: "0.1rem",
                  }}
                />
                <p
                  style={{
                    fontSize: isMobile ? "0.7rem" : "0.75rem",
                    color: getTextSecondary(),
                    margin: 0,
                    lineHeight: "1.4",
                    flex: 1,
                  }}
                >
                  If you don't have marks for any subject, enter{" "}
                  <strong style={{ color: "#8b5cf6" }}>0</strong>. Subjects with
                  0 credits are automatically excluded from calculation.
                </p>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setStep(2)}
                disabled={
                  !formData.branch ||
                  !formData.semester ||
                  (formData.semester <= 2 && !formData.cycle)
                }
                style={{
                  width: "100%",
                  padding: isMobile ? "0.7rem" : "0.8rem",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  color: "white",
                  border: "none",
                  borderRadius: "14px",
                  fontSize: isMobile ? "0.85rem" : "0.9rem",
                  fontWeight: "bold",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  transition: "all 0.3s",
                  opacity:
                    !formData.branch ||
                    !formData.semester ||
                    (formData.semester <= 2 && !formData.cycle)
                      ? 0.5
                      : 1,
                  pointerEvents:
                    !formData.branch ||
                    !formData.semester ||
                    (formData.semester <= 2 && !formData.cycle)
                      ? "none"
                      : "auto",
                  boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
                }}
              >
                Next: Enter Marks <FaArrowRight size={isMobile ? 12 : 14} />
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Marks Entry */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "28px",
              boxShadow: isDarkMode
                ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                : "0 25px 50px -12px rgba(0,0,0,0.25)",
              overflow: "hidden",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
                padding: isMobile ? "1rem" : "1.5rem",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "0.8rem",
                color: "white",
              }}
            >
              <div>
                <h2
                  style={{
                    fontSize: isMobile ? "1.1rem" : "1.2rem",
                    fontWeight: "bold",
                    marginBottom: "0.3rem",
                    color: "white",
                  }}
                >
                  Enter Your Marks
                </h2>
                <p
                  style={{
                    color: "rgba(255,255,255,0.8)",
                    fontSize: isMobile ? "0.7rem" : "0.75rem",
                  }}
                >
                  {getBranchName(formData.branch)} • Semester{" "}
                  {formData.semester}
                  {formData.cycle &&
                    ` • ${formData.cycle === "P" ? "Physics Cycle" : "Chemistry Cycle"}`}
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setStep(1)}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  color: "white",
                  width: isMobile ? "32px" : "36px",
                  height: isMobile ? "32px" : "36px",
                  borderRadius: "10px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.3s",
                }}
              >
                <FaArrowLeft size={isMobile ? 12 : 14} />
              </motion.button>
            </div>

            <div style={{ padding: cardPadding }}>
              {/* Student Details */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "0.8rem",
                  marginBottom: "1.5rem",
                  padding: "0.8rem",
                  background: isDarkMode
                    ? "rgba(139,92,246,0.1)"
                    : "rgba(139,92,246,0.05)",
                  borderRadius: "16px",
                  border: `1px solid ${getBorderColor()}`,
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      color: getTextSecondary(),
                      fontSize: isMobile ? "0.65rem" : "0.7rem",
                      fontWeight: "500",
                      marginBottom: "0.2rem",
                    }}
                  >
                    <FaUser
                      style={{
                        marginRight: "0.2rem",
                        color: "#8b5cf6",
                        display: "inline",
                      }}
                    />
                    Student Name
                  </label>
                  <input
                    type="text"
                    value={studentDetails.name}
                    onChange={(e) =>
                      setStudentDetails({
                        ...studentDetails,
                        name: e.target.value,
                      })
                    }
                    placeholder="Enter your name"
                    style={{
                      width: "100%",
                      padding: isMobile ? "0.5rem" : "0.6rem",
                      border: `2px solid ${getBorderColor()}`,
                      borderRadius: "10px",
                      fontSize: isMobile ? "0.75rem" : "0.8rem",
                      outline: "none",
                      transition: "all 0.3s",
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                    onBlur={(e) =>
                      (e.target.style.borderColor = getBorderColor())
                    }
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      color: getTextSecondary(),
                      fontSize: isMobile ? "0.65rem" : "0.7rem",
                      fontWeight: "500",
                      marginBottom: "0.2rem",
                    }}
                  >
                    <FaIdCard
                      style={{
                        marginRight: "0.2rem",
                        color: "#8b5cf6",
                        display: "inline",
                      }}
                    />
                    USN
                  </label>
                  <input
                    type="text"
                    value={studentDetails.usn}
                    onChange={(e) =>
                      setStudentDetails({
                        ...studentDetails,
                        usn: e.target.value,
                      })
                    }
                    placeholder="Enter your USN"
                    style={{
                      width: "100%",
                      padding: isMobile ? "0.5rem" : "0.6rem",
                      border: `2px solid ${getBorderColor()}`,
                      borderRadius: "10px",
                      fontSize: isMobile ? "0.75rem" : "0.8rem",
                      outline: "none",
                      transition: "all 0.3s",
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                    onBlur={(e) =>
                      (e.target.style.borderColor = getBorderColor())
                    }
                  />
                </div>
              </div>

              {/* Subjects and Marks */}
              {loading ? (
                <div style={{ textAlign: "center", padding: "2rem" }}>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    <FaSpinner size={32} color="#8b5cf6" />
                  </motion.div>
                  <p
                    style={{
                      marginTop: "0.8rem",
                      color: getTextSecondary(),
                      fontSize: isMobile ? "0.75rem" : "0.8rem",
                    }}
                  >
                    Loading subjects...
                  </p>
                </div>
              ) : subjects.length === 0 ? (
                <div style={{ textAlign: "center", padding: "2rem" }}>
                  <FaBook size={40} color={getTextSecondary()} />
                  <p
                    style={{
                      color: getTextSecondary(),
                      fontSize: isMobile ? "0.8rem" : "0.85rem",
                      marginTop: "0.8rem",
                    }}
                  >
                    No subjects found for the selected criteria.
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setStep(1)}
                    style={{
                      marginTop: "1rem",
                      padding: "0.5rem 1.2rem",
                      background: "none",
                      border: `2px solid #8b5cf6`,
                      color: "#8b5cf6",
                      borderRadius: "10px",
                      cursor: "pointer",
                      fontWeight: "500",
                      fontSize: isMobile ? "0.75rem" : "0.8rem",
                    }}
                  >
                    Go back and change selection
                  </motion.button>
                </div>
              ) : (
                <>
                  <div
                    style={{
                      maxHeight: isMobile ? "350px" : "450px",
                      overflowY: "auto",
                      paddingRight: isMobile ? "0.3rem" : "0.5rem",
                    }}
                  >
                    {subjects.map((subject, idx) => (
                      <motion.div
                        key={subject.subjectCode}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.03 }}
                        style={{
                          border: `1px solid ${getBorderColor()}`,
                          borderRadius: "14px",
                          padding: "0.8rem",
                          marginBottom: "0.8rem",
                          transition: "all 0.3s",
                          background: getCardBackground(),
                        }}
                        whileHover={{
                          boxShadow: "0 4px 12px rgba(139,92,246,0.1)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "0.8rem",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                                flexWrap: "wrap",
                                marginBottom: "0.3rem",
                              }}
                            >
                              <span
                                style={{
                                  fontFamily: "monospace",
                                  fontSize: isMobile ? "0.7rem" : "0.75rem",
                                  background: isDarkMode
                                    ? "rgba(139,92,246,0.1)"
                                    : "rgba(139,92,246,0.05)",
                                  padding: "0.2rem 0.5rem",
                                  borderRadius: "15px",
                                  color: "#8b5cf6",
                                }}
                              >
                                {subject.subjectCode}
                              </span>
                              <span
                                style={{
                                  fontSize: isMobile ? "0.65rem" : "0.7rem",
                                  color: getTextSecondary(),
                                }}
                              >
                                ({subject.credits} credits)
                              </span>
                            </div>
                            <p
                              style={{
                                fontSize: isMobile ? "0.8rem" : "0.85rem",
                                fontWeight: "500",
                                color: getTextColor(),
                                margin: 0,
                              }}
                            >
                              {subject.subjectName}
                            </p>
                          </div>
                          <div style={{ width: isMobile ? "100px" : "120px" }}>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max="200"
                              value={marks[subject.subjectCode] || ""}
                              onChange={(e) =>
                                handleMarksChange(
                                  subject.subjectCode,
                                  e.target.value,
                                )
                              }
                              placeholder="Marks"
                              style={{
                                width: "100%",
                                padding: isMobile ? "0.5rem" : "0.6rem",
                                border: `2px solid ${getBorderColor()}`,
                                borderRadius: "10px",
                                textAlign: "center",
                                fontSize: isMobile ? "0.75rem" : "0.8rem",
                                outline: "none",
                                transition: "all 0.3s",
                                background: getInputBackground(),
                                color: getTextColor(),
                              }}
                              onFocus={(e) =>
                                (e.target.style.borderColor = "#8b5cf6")
                              }
                              onBlur={(e) =>
                                (e.target.style.borderColor = getBorderColor())
                              }
                            />
                            <p
                              style={{
                                fontSize: isMobile ? "0.55rem" : "0.6rem",
                                color: getTextSecondary(),
                                textAlign: "center",
                                marginTop: "0.2rem",
                              }}
                            >
                              (0-200)
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "0.8rem",
                      marginTop: "1rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setStep(1)}
                      style={{
                        flex: 1,
                        padding: isMobile ? "0.6rem" : "0.7rem",
                        background: isDarkMode
                          ? "rgba(139,92,246,0.1)"
                          : "rgba(139,92,246,0.05)",
                        border: `2px solid ${getBorderColor()}`,
                        borderRadius: "12px",
                        fontSize: isMobile ? "0.8rem" : "0.85rem",
                        fontWeight: "500",
                        cursor: "pointer",
                        color: getTextColor(),
                        transition: "all 0.3s",
                      }}
                    >
                      Back
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={calculateSGPA}
                      disabled={calculating}
                      style={{
                        flex: 1,
                        padding: isMobile ? "0.6rem" : "0.7rem",
                        background: calculating
                          ? "#94a3b8"
                          : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                        color: "white",
                        border: "none",
                        borderRadius: "12px",
                        fontSize: isMobile ? "0.8rem" : "0.85rem",
                        fontWeight: "bold",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.5rem",
                        transition: "all 0.3s",
                        boxShadow: calculating
                          ? "none"
                          : "0 4px 14px 0 rgba(139,92,246,0.4)",
                      }}
                    >
                      {calculating ? (
                        <FaSpinner
                          style={{ animation: "spin 1s linear infinite" }}
                        />
                      ) : (
                        <FaCalculator />
                      )}
                      Calculate SGPA
                    </motion.button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}

        {/* Step 3: Results */}
        {step === 3 && result && (
          <motion.div
            ref={resultRef}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, type: "spring", stiffness: 300 }}
            style={{
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "28px",
              boxShadow: isDarkMode
                ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                : "0 25px 50px -12px rgba(0,0,0,0.25)",
              overflow: "hidden",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            {/* Result Header */}
            <div
              style={{
                background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
                padding: isMobile ? "1rem" : "1.5rem",
                color: "white",
              }}
            >
              <h2
                style={{
                  fontSize: isMobile ? "1.1rem" : "1.2rem",
                  fontWeight: "bold",
                  marginBottom: "0.8rem",
                  color: "white",
                }}
              >
                VTUGenX SGPA Grade Card
              </h2>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "0.8rem",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.6rem" : "0.65rem",
                      opacity: 0.8,
                      color: "rgba(255,255,255,0.8)",
                    }}
                  >
                    Student Name
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.85rem",
                      fontWeight: "bold",
                      color: "white",
                    }}
                  >
                    {studentDetails.name || "Not Specified"}
                  </div>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.6rem" : "0.65rem",
                      opacity: 0.8,
                      color: "rgba(255,255,255,0.8)",
                    }}
                  >
                    USN
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.85rem",
                      fontWeight: "bold",
                      color: "white",
                    }}
                  >
                    {studentDetails.usn || "Not Specified"}
                  </div>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.6rem" : "0.65rem",
                      opacity: 0.8,
                      color: "rgba(255,255,255,0.8)",
                    }}
                  >
                    Branch
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.85rem",
                      fontWeight: "bold",
                      color: "white",
                    }}
                  >
                    {getBranchName(formData.branch)}
                  </div>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.6rem" : "0.65rem",
                      opacity: 0.8,
                      color: "rgba(255,255,255,0.8)",
                    }}
                  >
                    Semester
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.85rem",
                      fontWeight: "bold",
                      color: "white",
                    }}
                  >
                    {formData.semester}
                    {formData.cycle &&
                      ` (${formData.cycle === "P" ? "Physics Cycle" : "Chemistry Cycle"})`}
                  </div>
                </div>
              </div>
            </div>

            {/* SGPA and Percentage */}
            <div
              style={{
                padding: isMobile ? "1.5rem" : "2rem",
                textAlign: "center",
                borderBottom: `1px solid ${getBorderColor()}`,
              }}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 400, damping: 20 }}
                style={{
                  fontSize: isMobile ? "3rem" : "4rem",
                  fontWeight: "bold",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  marginBottom: "0.3rem",
                }}
              >
                {result.sgpa}
              </motion.div>
              <div
                style={{
                  fontSize: isMobile ? "0.7rem" : "0.75rem",
                  color: getTextSecondary(),
                  marginBottom: "0.8rem",
                }}
              >
                SGPA out of 10
              </div>

              <div
                style={{
                  fontSize: isMobile ? "1.5rem" : "1.8rem",
                  fontWeight: "bold",
                  color: getTextColor(),
                  marginBottom: "0.8rem",
                }}
              >
                {result.percentage}%
              </div>

              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2 }}
                style={{
                  display: "inline-block",
                  padding: isMobile ? "0.5rem 1.5rem" : "0.6rem 2rem",
                  borderRadius: "40px",
                  background: result.classAwarded.includes("Distinction")
                    ? "linear-gradient(135deg, #8b5cf6, #6d28d9)"
                    : result.classAwarded.includes("First Class")
                      ? "linear-gradient(135deg, #10b981, #059669)"
                      : result.classAwarded.includes("Second Class")
                        ? "linear-gradient(135deg, #3b82f6, #1d4ed8)"
                        : result.classAwarded.includes("Pass")
                          ? "linear-gradient(135deg, #f59e0b, #d97706)"
                          : "linear-gradient(135deg, #ef4444, #dc2626)",
                  color: "white",
                  fontWeight: "bold",
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  marginTop: "0.3rem",
                }}
              >
                {result.classAwarded}
              </motion.div>

              <div
                style={{
                  background: isDarkMode
                    ? "rgba(139,92,246,0.1)"
                    : "rgba(139,92,246,0.05)",
                  borderRadius: "12px",
                  padding: "0.6rem",
                  marginTop: "1rem",
                  display: "inline-block",
                  border: `1px solid ${getBorderColor()}`,
                }}
              >
                <p
                  style={{
                    color: getTextSecondary(),
                    fontSize: isMobile ? "0.65rem" : "0.7rem",
                    fontFamily: "monospace",
                    margin: 0,
                  }}
                >
                  Formula: {result.formula}
                </p>
              </div>
            </div>

            {/* Subject-wise Table */}
            <div style={{ padding: cardPadding }}>
              <h3
                style={{
                  fontSize: isMobile ? "0.9rem" : "1rem",
                  fontWeight: "bold",
                  color: getTextColor(),
                  marginBottom: "0.8rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <FaBook size={14} color="#8b5cf6" /> Subject-wise Performance
              </h3>
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: isMobile ? "0.7rem" : "0.75rem",
                    minWidth: "500px",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: isDarkMode
                          ? "rgba(139,92,246,0.1)"
                          : "rgba(139,92,246,0.05)",
                        borderBottom: `2px solid ${getBorderColor()}`,
                      }}
                    >
                      <th
                        style={{
                          padding: "0.6rem",
                          textAlign: "left",
                          color: getTextColor(),
                        }}
                      >
                        Subject Code
                      </th>
                      <th
                        style={{
                          padding: "0.6rem",
                          textAlign: "left",
                          color: getTextColor(),
                        }}
                      >
                        Subject Name
                      </th>
                      <th
                        style={{
                          padding: "0.6rem",
                          textAlign: "center",
                          color: getTextColor(),
                        }}
                      >
                        Marks
                      </th>
                      <th
                        style={{
                          padding: "0.6rem",
                          textAlign: "center",
                          color: getTextColor(),
                        }}
                      >
                        Grade
                      </th>
                      <th
                        style={{
                          padding: "0.6rem",
                          textAlign: "center",
                          color: getTextColor(),
                        }}
                      >
                        Credits
                      </th>
                      <th
                        style={{
                          padding: "0.6rem",
                          textAlign: "center",
                          color: getTextColor(),
                        }}
                      >
                        Points
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.subjectResults.map((subject, idx) => (
                      <tr
                        key={idx}
                        style={{
                          borderBottom: `1px solid ${getBorderColor()}`,
                        }}
                      >
                        <td
                          style={{
                            padding: "0.6rem",
                            fontFamily: "monospace",
                            color: getTextColor(),
                          }}
                        >
                          {subject.subjectCode}
                        </td>
                        <td
                          style={{ padding: "0.6rem", color: getTextColor() }}
                        >
                          {subject.subjectName}
                        </td>
                        <td
                          style={{
                            padding: "0.6rem",
                            textAlign: "center",
                            color: getTextColor(),
                          }}
                        >
                          {subject.marks}
                        </td>
                        <td
                          style={{
                            padding: "0.6rem",
                            textAlign: "center",
                            fontWeight: "bold",
                            color: "#8b5cf6",
                          }}
                        >
                          {subject.gradePoint}
                        </td>
                        <td
                          style={{
                            padding: "0.6rem",
                            textAlign: "center",
                            color: getTextColor(),
                          }}
                        >
                          {subject.credits}
                        </td>
                        <td
                          style={{
                            padding: "0.6rem",
                            textAlign: "center",
                            color: getTextColor(),
                          }}
                        >
                          {subject.points}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot
                    style={{
                      background: isDarkMode
                        ? "rgba(139,92,246,0.1)"
                        : "rgba(139,92,246,0.05)",
                      borderTop: `2px solid ${getBorderColor()}`,
                    }}
                  >
                    <tr>
                      <td
                        colSpan="4"
                        style={{
                          padding: "0.6rem",
                          textAlign: "right",
                          fontWeight: "bold",
                          color: getTextColor(),
                        }}
                      >
                        Total:
                      </td>
                      <td
                        style={{
                          padding: "0.6rem",
                          textAlign: "center",
                          fontWeight: "bold",
                          color: getTextColor(),
                        }}
                      >
                        {result.totalCredits}
                      </td>
                      <td
                        style={{
                          padding: "0.6rem",
                          textAlign: "center",
                          fontWeight: "bold",
                          color: "#8b5cf6",
                        }}
                      >
                        {result.totalPoints}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Action Buttons */}
            <div
              className="action-buttons"
              style={{
                padding: isMobile ? "0.8rem" : "1rem",
                background: isDarkMode
                  ? "rgba(139,92,246,0.05)"
                  : "rgba(139,92,246,0.03)",
                display: "flex",
                flexWrap: "wrap",
                gap: "0.5rem",
                borderTop: `1px solid ${getBorderColor()}`,
              }}
            >
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={downloadResult}
                style={{
                  flex: 1,
                  padding: isMobile ? "0.5rem" : "0.6rem",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.3rem",
                  fontWeight: "bold",
                  fontSize: isMobile ? "0.7rem" : "0.75rem",
                }}
              >
                <FaDownload size={isMobile ? 10 : 12} /> Download PDF
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={printResult}
                style={{
                  flex: 1,
                  padding: isMobile ? "0.5rem" : "0.6rem",
                  background: isDarkMode ? "rgba(100,116,139,0.3)" : "#e2e8f0",
                  color: getTextColor(),
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.3rem",
                  fontWeight: "bold",
                  fontSize: isMobile ? "0.7rem" : "0.75rem",
                }}
              >
                <FaPrint size={isMobile ? 10 : 12} /> Print
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={shareResult}
                style={{
                  flex: 1,
                  padding: isMobile ? "0.5rem" : "0.6rem",
                  background: "linear-gradient(135deg, #10b981, #059669)",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.3rem",
                  fontWeight: "bold",
                  fontSize: isMobile ? "0.7rem" : "0.75rem",
                }}
              >
                {copied ? (
                  <FaCheck size={isMobile ? 10 : 12} />
                ) : (
                  <FaShare size={isMobile ? 10 : 12} />
                )}{" "}
                Share
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={resetCalculator}
                style={{
                  flex: 1,
                  padding: isMobile ? "0.5rem" : "0.6rem",
                  background: isDarkMode
                    ? "rgba(139,92,246,0.1)"
                    : "rgba(139,92,246,0.05)",
                  color: getTextColor(),
                  border: `1px solid ${getBorderColor()}`,
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontWeight: "bold",
                  fontSize: isMobile ? "0.7rem" : "0.75rem",
                }}
              >
                New Calculation
              </motion.button>
            </div>

            {/* Footer Note - Kept only this verification note */}
            <div
              style={{
                padding: "0.6rem",
                textAlign: "center",
                fontSize: isMobile ? "0.55rem" : "0.6rem",
                color: getTextSecondary(),
                borderTop: `1px solid ${getBorderColor()}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
              }}
            >
              <FaShieldAlt size={10} />
              The results are derived based on VTU guidelines and are subject to
              verification.
            </div>
          </motion.div>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default SGPACalculator;

